import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Send, Bot, User, Loader2, Sparkles, Shield,
  Brain, Zap, AlertTriangle, CheckCircle, Clock, RefreshCw, X, Trash2
} from 'lucide-react';
import ParticleBackground from '../components/ParticleBackground';
import AnimatedBackground from '../components/AnimatedBackground';
import GlowingCard from '../components/GlowingCard';
import ChatMarkdown from '../components/ChatMarkdown';
import { useDemoSession } from '../hooks/useDemoSession';

export default function DemoPage() {
  const {
    session,
    messages,
    requests,
    isLoading,
    error,
    initSession,
    sendMessage,
    retryMessage,
    dismissFailedMessage,
    submitRequest,
    clearSession,
  } = useDemoSession();

  const [input, setInput] = useState('');
  const [activeTab, setActiveTab] = useState('chat');
  const messagesEndRef = useRef(null);

  // Initialize session on mount
  useEffect(() => {
    initSession();
  }, [initSession]);

  // Auto-clear chat when user leaves the page
  useEffect(() => {
    const handleBeforeUnload = () => {
      clearSession();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handleBeforeUnload);
      // Also clear on component unmount (navigation away)
      clearSession();
    };
  }, [clearSession]);

  // Auto-scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Clear chat and start fresh
  const handleClearChat = () => {
    clearSession();
    initSession();
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const message = input.trim();
    setInput('');

    try {
      await sendMessage(message);
    } catch (err) {
      console.error('Failed to send message:', err);
    }
  };

  const handleQuickAction = async (action) => {
    if (isLoading) return;

    // Detailed prompts sent to Claude for better responses
    const prompts = {
      intro: "Hi Nellie! Can you explain what the CAO Platform does and how AI governance works?",
      governance: "Walk me through a typical governance request. How does the executive council evaluate AI agent actions?",
      risk: "How does the CAO Platform assess and manage risk levels for autonomous AI decisions?",
      compliance: "What compliance and audit capabilities does the platform provide for enterprise environments?",
    };

    // Short labels shown in the chat UI (matching button text)
    const labels = {
      intro: "What is CAO Platform?",
      governance: "How does governance work?",
      risk: "Risk assessment",
      compliance: "Compliance features",
    };

    const prompt = prompts[action];
    const label = labels[action];
    if (prompt && label) {
      try {
        // Send detailed prompt to API, but show short label in chat
        await sendMessage(prompt, label);
      } catch (err) {
        console.error('Failed to send quick action message:', err);
      }
    }
  };

  const handleGovernanceDemo = async () => {
    try {
      await submitRequest({
        requestType: 'DEPLOYMENT',
        agentName: 'Marketing AI Assistant',
        description: 'Request to deploy an AI agent that will autonomously send marketing emails to customer segments based on behavioral analysis.',
        riskLevel: 'MEDIUM',
        autonomyTier: 'SUPERVISED',
      });
    } catch (err) {
      console.error('Failed to submit governance request:', err);
    }
  };

  const suggestedQuestions = [
    { key: 'intro', label: 'What is CAO Platform?', icon: Sparkles },
    { key: 'governance', label: 'How does governance work?', icon: Shield },
    { key: 'risk', label: 'Risk assessment', icon: AlertTriangle },
    { key: 'compliance', label: 'Compliance features', icon: CheckCircle },
  ];

  return (
    <div className="relative min-h-screen pt-20">
      <AnimatedBackground variant="gaming" />
      <ParticleBackground density={10} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
          <div>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-2 sm:mb-4 text-sm sm:text-base"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
            <h1 className="text-2xl sm:text-3xl font-bold text-white">Interactive Demo</h1>
            <p className="text-slate-400 text-sm sm:text-base">
              Chat with Nellie, our AI Chief Autonomy Officer
            </p>
          </div>
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-green-500/10 border border-green-500/30">
              <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <span className="text-xs sm:text-sm text-green-400">Live Demo</span>
            </div>
            <button
              onClick={handleClearChat}
              className="p-2 rounded-lg bg-slate-800/50 border border-slate-700 hover:border-red-500/50 transition-all"
              title="Clear Chat"
            >
              <Trash2 className="w-5 h-5 text-slate-400 hover:text-red-400" />
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Main Chat Area */}
          <div className="lg:col-span-2">
            <GlowingCard glowColor="blue">
              <div className="flex flex-col h-[calc(100vh-220px)] sm:h-[calc(100vh-240px)] min-h-[400px] max-h-[700px]">
                {/* Tab Navigation */}
                <div className="flex border-b border-slate-700/50">
                  <button
                    onClick={() => setActiveTab('chat')}
                    className={`flex-1 px-2 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm font-medium transition-colors ${
                      activeTab === 'chat'
                        ? 'text-blue-400 border-b-2 border-blue-400'
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-1.5 sm:gap-2">
                      <Bot className="w-4 h-4 shrink-0" />
                      <span className="truncate">Chat with Nellie</span>
                    </div>
                  </button>
                  <button
                    onClick={() => setActiveTab('governance')}
                    className={`flex-1 px-2 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm font-medium transition-colors ${
                      activeTab === 'governance'
                        ? 'text-purple-400 border-b-2 border-purple-400'
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-1.5 sm:gap-2">
                      <Shield className="w-4 h-4 shrink-0" />
                      <span className="truncate">Governance Requests</span>
                    </div>
                  </button>
                  {/* Clear chat button - only show when there are messages */}
                  {messages.length > 0 && activeTab === 'chat' && (
                    <button
                      onClick={handleClearChat}
                      className="px-3 py-2.5 sm:py-3 text-xs sm:text-sm font-medium text-slate-400 hover:text-red-400 transition-colors flex items-center gap-1.5"
                      title="Clear chat history"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span className="hidden sm:inline">Clear</span>
                    </button>
                  )}
                </div>

                {activeTab === 'chat' ? (
                  <>
                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto p-3 sm:p-6 space-y-3 sm:space-y-4">
                      {messages.length === 0 && (
                        <div className="text-center py-6 sm:py-12">
                          <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 border border-blue-500/30 flex items-center justify-center mx-auto mb-3 sm:mb-4">
                            <Bot className="w-6 h-6 sm:w-8 sm:h-8 text-blue-400" />
                          </div>
                          <h3 className="text-lg sm:text-xl font-semibold text-white mb-2">
                            Meet Nellie, Your CAO
                          </h3>
                          <p className="text-slate-400 text-sm sm:text-base max-w-md mx-auto mb-4 sm:mb-6 px-2">
                            I'm the AI Chief Autonomy Officer. Ask me anything about AI governance,
                            the executive council, or how the CAO Platform works.
                          </p>
                          <div className="flex flex-col sm:flex-row sm:flex-wrap justify-center gap-2 px-2">
                            {suggestedQuestions.map((q) => (
                              <button
                                key={q.key}
                                onClick={() => handleQuickAction(q.key)}
                                className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 rounded-lg bg-slate-800/50 border border-slate-700/50 hover:border-blue-500/50 text-sm text-slate-300 hover:text-white transition-all"
                              >
                                <q.icon className="w-4 h-4 shrink-0" />
                                {q.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {messages.map((msg, i) => (
                        <div
                          key={msg.id || i}
                          className={`flex gap-2 sm:gap-3 ${
                            msg.role === 'USER' ? 'flex-row-reverse' : ''
                          }`}
                        >
                          <div
                            className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center shrink-0 ${
                              msg.role === 'USER'
                                ? msg.failed
                                  ? 'bg-red-500/20 border border-red-500/30'
                                  : 'bg-blue-500/20 border border-blue-500/30'
                                : 'bg-purple-500/20 border border-purple-500/30'
                            }`}
                          >
                            {msg.role === 'USER' ? (
                              msg.failed ? (
                                <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-red-400" />
                              ) : (
                                <User className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" />
                              )
                            ) : (
                              <Bot className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400" />
                            )}
                          </div>
                          <div
                            className={`max-w-[85%] sm:max-w-[80%] p-3 sm:p-4 rounded-2xl ${
                              msg.role === 'USER'
                                ? msg.failed
                                  ? 'bg-red-500/10 border border-red-500/30'
                                  : 'bg-blue-500/10 border border-blue-500/30'
                                : 'bg-slate-800/50 border border-slate-700/50'
                            }`}
                          >
                            {msg.role === 'ASSISTANT' ? (
                              <ChatMarkdown content={msg.content} />
                            ) : (
                              <p className={`whitespace-pre-wrap text-sm sm:text-base ${
                                msg.failed ? 'text-red-300' : 'text-slate-200'
                              }`}>
                                {msg.content}
                              </p>
                            )}
                            {msg.failed && (
                              <div className="mt-2 pt-2 border-t border-red-500/30">
                                <p className="text-xs text-red-400 mb-2">
                                  Failed to send: {msg.error || 'Unknown error'}
                                </p>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => retryMessage(msg.id)}
                                    disabled={isLoading}
                                    className="flex items-center gap-1 px-2 py-1 text-xs rounded bg-red-500/20 hover:bg-red-500/30 text-red-300 transition-colors disabled:opacity-50"
                                  >
                                    <RefreshCw className="w-3 h-3" />
                                    Retry
                                  </button>
                                  <button
                                    onClick={() => dismissFailedMessage(msg.id)}
                                    className="flex items-center gap-1 px-2 py-1 text-xs rounded bg-slate-700/50 hover:bg-slate-700 text-slate-400 transition-colors"
                                  >
                                    <X className="w-3 h-3" />
                                    Dismiss
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}

                      {isLoading && (
                        <div className="flex gap-2 sm:gap-3">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-purple-500/20 border border-purple-500/30 flex items-center justify-center shrink-0">
                            <Bot className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400" />
                          </div>
                          <div className="max-w-[85%] sm:max-w-[80%] p-3 sm:p-4 rounded-2xl bg-slate-800/50 border border-slate-700/50">
                            <div className="flex items-center gap-2 text-slate-400 text-sm sm:text-base">
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Nellie is thinking...
                            </div>
                          </div>
                        </div>
                      )}

                      <div ref={messagesEndRef} />
                    </div>

                    {/* Input */}
                    <form onSubmit={handleSendMessage} className="p-3 sm:p-4 border-t border-slate-700/50">
                      <div className="flex gap-2 sm:gap-3">
                        <input
                          type="text"
                          value={input}
                          onChange={(e) => setInput(e.target.value)}
                          placeholder="Ask Nellie about AI governance.."
                          className="flex-1 min-w-0 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl bg-slate-800/50 border border-slate-700 text-white text-sm sm:text-base placeholder-slate-500 focus:outline-none focus:border-blue-500/50 transition-colors"
                          disabled={isLoading}
                        />
                        <button
                          type="submit"
                          disabled={isLoading || !input.trim()}
                          className="px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl font-semibold bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                        >
                          <Send className="w-5 h-5" />
                        </button>
                      </div>
                    </form>
                  </>
                ) : (
                  <div className="flex-1 overflow-y-auto p-3 sm:p-6">
                    <div className="text-center mb-4 sm:mb-6">
                      <h3 className="text-lg sm:text-xl font-semibold text-white mb-2">
                        Governance Request Simulator
                      </h3>
                      <p className="text-slate-400 text-sm sm:text-base mb-4">
                        See how the executive council evaluates AI agent requests
                      </p>
                      <button
                        onClick={handleGovernanceDemo}
                        disabled={isLoading}
                        className="px-5 sm:px-6 py-2.5 sm:py-3 rounded-xl font-semibold text-sm sm:text-base bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white transition-all disabled:opacity-50"
                      >
                        {isLoading ? (
                          <span className="flex items-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Processing...
                          </span>
                        ) : (
                          'Submit Sample Request'
                        )}
                      </button>
                    </div>

                    {/* Requests List */}
                    <div className="space-y-3 sm:space-y-4">
                      {requests.map((req) => (
                        <div
                          key={req.id}
                          className="p-3 sm:p-4 rounded-xl bg-slate-800/50 border border-slate-700/50"
                        >
                          <div className="flex items-start sm:items-center justify-between gap-2 mb-2 sm:mb-3">
                            <h4 className="font-semibold text-white text-sm sm:text-base">{req.agentName}</h4>
                            <span
                              className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium shrink-0 ${
                                req.status === 'APPROVED'
                                  ? 'bg-green-500/10 text-green-400 border border-green-500/30'
                                  : req.status === 'REJECTED'
                                  ? 'bg-red-500/10 text-red-400 border border-red-500/30'
                                  : 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/30'
                              }`}
                            >
                              {req.status}
                            </span>
                          </div>
                          <p className="text-xs sm:text-sm text-slate-400 mb-2">{req.requestType}</p>
                          <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs text-slate-500">
                            <span>Risk: {req.riskLevel}</span>
                            <span>Autonomy: {req.autonomyTier?.replace('_', ' ')}</span>
                          </div>
                        </div>
                      ))}

                      {requests.length === 0 && (
                        <p className="text-center text-slate-500 text-sm sm:text-base py-6 sm:py-8">
                          No governance requests yet. Click the button above to simulate one.
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </GlowingCard>
          </div>

          {/* Sidebar */}
          <div className="space-y-4 sm:space-y-6">
            {/* Platform Features */}
            <GlowingCard glowColor="purple">
              <div className="p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4">Platform Highlights</h3>
                <div className="grid grid-cols-2 lg:grid-cols-1 gap-3 sm:gap-4">
                  {[
                    { icon: Brain, title: 'Executive AI Council', desc: 'CEO, CTO, CPO, CFO, CLO, CISO agents' },
                    { icon: Shield, title: 'Governance Engine', desc: 'Real-time decision orchestration' },
                    { icon: Zap, title: 'Autonomy Tiers', desc: 'Adaptive control levels' },
                    { icon: Clock, title: 'Audit Trail', desc: 'Complete decision history' },
                  ].map((feature, i) => (
                    <div key={i} className="flex items-start gap-2 sm:gap-3">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-purple-500/10 border border-purple-500/30 flex items-center justify-center shrink-0">
                        <feature.icon className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400" />
                      </div>
                      <div className="min-w-0">
                        <h4 className="font-medium text-white text-sm sm:text-base truncate">{feature.title}</h4>
                        <p className="text-xs sm:text-sm text-slate-400 line-clamp-2">{feature.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </GlowingCard>

            {/* CTA */}
            <GlowingCard glowColor="cyan">
              <div className="p-4 sm:p-6 text-center">
                <h3 className="text-base sm:text-lg font-semibold text-white mb-2">
                  Ready to Get Started?
                </h3>
                <p className="text-xs sm:text-sm text-slate-400 mb-3 sm:mb-4">
                  Schedule a personalized demo with our team
                </p>
                <Link
                  to="/signup"
                  className="block w-full py-2.5 sm:py-3 rounded-xl font-semibold text-sm sm:text-base bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white transition-all"
                >
                  Request Full Demo
                </Link>
              </div>
            </GlowingCard>

            {error && (
              <div className="p-3 sm:p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-xs sm:text-sm">
                {error}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
